import React, { useState } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'app/helpers/routing';
import ClearableTextField from 'app/components/shared/TextField/ClearableTextField';
import { Filter, ShelfMap } from '@bibliocommons/base-icons';
import { SolidButton, SecondaryGhostButton } from '@bibliocommons/base-buttons';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';

import ModalActions from 'app/actions/ModalActions';
import { selectLocationEntities } from 'app/selectors/EntitiesSelector';
import ModalContainer from 'app/components/shared/Modal/ModalContainer';
import LocationsSearchFilters from './LocationsSearchFilters';
import OpenNowFilter from './OpenNowFilter';

import './LocationsSearchForm.scss';

const FILTERS_MODAL_KEY = 'locations-filters-modal';

export default function LocationsSearchForm({ activeFilters }) {
  const router = useRouter();
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState(router.query.name || router.query.address);
  const allLocations = useSelector(selectLocationEntities);
  const [isLoading, setIsLoading] = useState(false);
  const facilitiesCount = activeFilters.get('facilities')?.size || 0;
  const filtersCount = activeFilters.get('open') ? facilitiesCount + 1 : facilitiesCount;

  function openFiltersModal() {
    dispatch(ModalActions.openModal(FILTERS_MODAL_KEY, { authRequired: false }));
  }

  function closeFiltersModal() {
    dispatch(ModalActions.closeModal(FILTERS_MODAL_KEY));
  }

  function handleGeoSearch() {
    setIsLoading(true);
    setSearchTerm('');
    const options = {
      timeout: 5000,
      maximumAge: 0
    };

    function success(pos) {
      setIsLoading(false);
      const { longitude, latitude } = pos.coords;
      router.push({
        query: { near: `${longitude},${latitude}` }
      });
    }

    function error(err) {
      setIsLoading(false);
      console.error(err); // eslint-disable-line no-console
    }

    navigator.geolocation.getCurrentPosition(success, error, options);
  }

  function handleSearch() {
    const regex = new RegExp(searchTerm, 'i');
    if (allLocations.some(location => regex.test(location.get('name')))) {
      router.push({
        query: { name: searchTerm }
      });
    } else {
      router.push({
        query: { address: searchTerm }
      });
    }
  }

  const handleKeypress = e => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleChange = value => {
    setSearchTerm(value);
  };

  function handleClear() {
    router.replace({ query: null });
    setSearchTerm('');
  }

  return (
    <div className="cp-locations-search-form">
      <div className="form-row">
        <div className="form-row-col">
          <div className="search">
            <ClearableTextField
              id="locations-search-term"
              label={<FormattedMessage id="location_search_label" />}
              value={searchTerm || ''}
              onChange={handleChange}
              onKeyPress={handleKeypress}
              handleClear={handleClear}
              hideClearButton={!searchTerm}
            />
            <SolidButton className="search-button" dataKey="search-button" handleClick={handleSearch}>
              <FormattedMessage id="button_search" />
            </SolidButton>
          </div>

          <SecondaryGhostButton
            className="use-my-location"
            dataKey="current-location-button"
            renderLeadingIcon={() => <ShelfMap />}
            handleClick={handleGeoSearch}
            isLoading={isLoading}
          >
            <FormattedMessage id="use_my_location" />
          </SecondaryGhostButton>
        </div>

        <div className="form-row-col">
          <div className="filter-actions">
            <SecondaryGhostButton
              dataKey="filter-button"
              renderLeadingIcon={() => <Filter />}
              handleClick={openFiltersModal}
            >
              {filtersCount > 0 ? (
                <>
                  <span aria-hidden>
                    <FormattedMessage id="filter_count" values={{ count: filtersCount }} />
                  </span>
                  <ScreenReaderMessage>
                    <FormattedMessage id="sr_filter_locations_count" values={{ count: filtersCount }} />
                  </ScreenReaderMessage>
                </>
              ) : (
                <>
                  <span aria-hidden>
                    <FormattedMessage id="filter" />
                  </span>
                  <ScreenReaderMessage>
                    <FormattedMessage id="sr_filter_locations_opens_overlay" />
                  </ScreenReaderMessage>
                </>
              )}
            </SecondaryGhostButton>
            <OpenNowFilter id="form-open-now" />
          </div>
        </div>
      </div>

      <ModalContainer modalKey={FILTERS_MODAL_KEY} handleClose={closeFiltersModal}>
        <LocationsSearchFilters handleClose={closeFiltersModal} />
      </ModalContainer>
    </div>
  );
}

LocationsSearchForm.propTypes = {
  activeFilters: ImmutablePropTypes.map.isRequired
};
